import React, { useRef, useState } from 'react';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  AccordionContainer,
  StyledComment,
} from 'components/molecules/ThreadAccordion/styles';
import { Grid, Link, Typography } from '@mui/material';
import { colors } from 'theme/colors';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { IApplicationComments, ICommentForThreadFormValues } from 'models/cases';
import { useTranslations } from 'hooks/useTranslations';
import { useScrollToLatestItem } from 'hooks/useScrollToLatestItem';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { useCase } from '../../hooks';
import { useParams } from 'react-router-dom';
import { useAuth } from 'models/authContext';
import { CommentForm } from './CommentForm';
import { NoComments } from '../../styles';

export interface ICommentsAccordions {
  items: IApplicationComments[];
  maxVisibleItems?: number;
  defaultExpandedIndex?: number | false;
  reloadApplicationCommentsData: () => void;
  replyThreadId: string | null;
  setReplyThreadId: (arg: string | null) => void;
  isCommentForm: boolean;
  canManageComments: boolean;
  editCommentId: string | null;
  setEditCommentId: (arg: string | null) => void;
  editThreadId: string | null;
  setEditThreadId: (arg: string | null) => void;
}

export const CommentsAccordions: React.FC<ICommentsAccordions> = ({
  items,
  maxVisibleItems,
  defaultExpandedIndex = false,
  reloadApplicationCommentsData,
  replyThreadId,
  setReplyThreadId,
  isCommentForm,
  canManageComments,
  editCommentId,
  setEditCommentId,
  editThreadId,
  setEditThreadId,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslations();
  const { fileId = '' } = useParams<{ fileId: string }>();
  const [expanded, setExpanded] = useState<number | false>(defaultExpandedIndex);
  const { shortDateFormat, longDateFormat } = useGlobalProperty();
  const { addApplicationCommentForThread, editApplicationCommentForThread } = useCase();
  const { user } = useAuth();

  useScrollToLatestItem(items, containerRef, setExpanded);

  const closeCommentForm = () => {
    setReplyThreadId(null);
    setEditCommentId(null);
  };

  const submitAddCommentForm = async (data: ICommentForThreadFormValues) => {
    if (replyThreadId) {
      await addApplicationCommentForThread(fileId, replyThreadId, data);
      closeCommentForm();
      reloadApplicationCommentsData();
    }
  };
  const submitEditCommentForm = async (data: ICommentForThreadFormValues) => {
    if (editCommentId) {
      await editApplicationCommentForThread(fileId, editCommentId, data);
      closeCommentForm();
      reloadApplicationCommentsData();
    }
  };

  const handleChange =
    (index: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? index : false);
      setEditCommentId(null);
      setReplyThreadId(null);
    };

  if (!items.length) {
    return <NoComments>{t('general.noCommentsAdded.label')}</NoComments>;
  }

  return (
    <AccordionContainer maxVisibleItems={maxVisibleItems} ref={containerRef}>
      {items.map((item, index) => (
        <StyledAccordion
          key={item.uniqueId}
          expanded={expanded === index}
          onChange={handleChange(index)}
        >
          <StyledAccordionSummary
            expandIcon={
              <>
                <span className="icon-plus">
                  <Icon type={EIconTypes.plus} fontSize={11} color={colors.black} />
                </span>

                <span className="icon-minus">
                  <Icon type={EIconTypes.minus} fontSize={11} color={colors.black} />
                </span>
              </>
            }
            aria-controls={`panel${index}-content`}
            id={`panel${index}-header`}
          >
            <Grid sx={{ width: '100%', minWidth: 0 }}>
              <Grid sx={{ display: 'flex', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: colors.primary900,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    flexGrow: 1,
                    minWidth: 0,
                  }}
                >
                  {item.topic}
                </Typography>
                {user?.userId === item.createdBy &&
                  canManageComments &&
                  editThreadId !== item.uniqueId &&
                  !editCommentId &&
                  !replyThreadId &&
                  !isCommentForm && (
                    <Typography variant="body2" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                      <Link
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditThreadId(item.uniqueId);
                        }}
                        underline="none"
                        rel="noopener noreferrer"
                        style={{
                          cursor: 'pointer',
                        }}
                      >
                        {t('general.edit.label')}
                      </Link>
                    </Typography>
                  )}
              </Grid>
              <Typography variant="caption" sx={{ color: colors.primary900 }}>
                {`${t('general.last.label')}: ${formatDate(item.lastCommentDate, longDateFormat)} ${t('general.by.label')} ${item.firstName} ${item.lastName} (${item.username})`}
              </Typography>
            </Grid>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            {item.comments.map((comment, index) => (
              <StyledComment first={index === 0}>
                <Grid sx={{ gap: 1, display: 'flex', flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2">
                    {`${formatDate(comment.createdDate, shortDateFormat)} ${comment.firstName} ${comment.lastName} (${comment.username})`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.gray400, fontStyle: 'italic' }}
                  >
                    {editCommentId === comment.uniqueId && t('general.inEdition.label')}
                    {editCommentId !== comment.uniqueId &&
                      comment.edited &&
                      t('general.edited.label')}
                  </Typography>
                  {user?.userId === comment.createdBy &&
                    canManageComments &&
                    editCommentId !== comment.uniqueId &&
                    !editThreadId &&
                    !replyThreadId &&
                    !isCommentForm && (
                      <Typography variant="body2">
                        <Link
                          onClick={() => setEditCommentId(comment.uniqueId)}
                          underline="none"
                          rel="noopener noreferrer"
                          style={{
                            cursor: 'pointer',
                          }}
                        >
                          {t('general.edit.label')}
                        </Link>
                      </Typography>
                    )}
                </Grid>
                <Grid sx={{ width: '100%', wordBreak: 'break-word' }}>
                  <Typography variant="body2">{getHtmlText(comment.comment)}</Typography>
                </Grid>
              </StyledComment>
            ))}
            <Grid sx={{ textAlign: 'right' }}>
              {(!replyThreadId || replyThreadId !== item.uniqueId) &&
                !editCommentId &&
                !editThreadId &&
                canManageComments && (
                  <Button
                    label={t('caseManagement.cases.replyInThread.label')}
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.small}
                    onClick={() => setReplyThreadId(item.uniqueId)}
                    disabled={isCommentForm}
                    id="comments_accordions_reply_in_thread-button"
                  />
                )}
            </Grid>
          </StyledAccordionDetails>
          <Grid sx={{ p: 1 }}>
            {replyThreadId === item.uniqueId && !isCommentForm && (
              <CommentForm
                submitForm={submitAddCommentForm}
                closeCommentForm={closeCommentForm}
              />
            )}
            {editCommentId && (!replyThreadId || replyThreadId !== item.uniqueId) && (
              <CommentForm
                submitForm={submitEditCommentForm}
                closeCommentForm={closeCommentForm}
                editFormValues={{
                  comment:
                    item.comments.find((comment) => comment.uniqueId === editCommentId)
                      ?.comment ?? '',
                }}
              />
            )}
          </Grid>
        </StyledAccordion>
      ))}
    </AccordionContainer>
  );
};
